// One tiny fetch wrapper. Every call refreshes the token (if within 5s of
// expiry) and attaches the bearer. Non-2xx throws so callers use try/catch
// or a react-query wrapper later. Kept dependency-free on purpose.

import { useMemo } from 'react';
import { triggerLogin, useAuth } from './auth';

export class ApiError extends Error {
    readonly status: number;
    readonly bodyText: string;
    constructor(status: number, bodyText: string) {
        super(`HTTP ${status}: ${bodyText.slice(0, 200)}`);
        this.status = status;
        this.bodyText = bodyText;
    }
}

async function request<T>(
    ensureFresh: () => Promise<void>,
    token: () => string | undefined,
    onAuthFail: () => void,
    method: string,
    path: string,
    body?: unknown,
): Promise<T> {
    await ensureFresh();
    const headers: Record<string, string> = {};
    const t = token();
    if (t) headers.Authorization = 'Bearer ' + t;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const res = await fetch(path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (res.status === 401 && t) {
        // Server rejected the JWT — belt + suspenders against a stale token
        // that survived ensureFresh (Keycloak sometimes returns the cached
        // access token without checking its own exp). Trigger a fresh
        // authorization-code flow. Caller still gets the error to swallow.
        // Anon requests (no token attached) never trigger login — a 401 from
        // a private endpoint hit by an anon caller is just an error the SPA
        // handles inline (e.g. showing a signup lure).
        onAuthFail();
    }
    if (!res.ok) {
        throw new ApiError(res.status, await res.text());
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
}

/**
 * Hook returning bound HTTP methods. The returned object is stable across
 * renders (only re-created when auth changes), so it's safe to list in
 * useEffect / useCallback dep arrays. Non-stable would cause infinite
 * refetch loops.
 */
export function useApi() {
    const { ensureFresh, token, kc } = useAuth();
    return useMemo(() => {
        // triggerLogin is module-scoped in auth.tsx — the flag is shared
        // across every useApi() consumer, so parallel 401s never stack up
        // multiple navigations.
        const onAuthFail = () => { if (kc) triggerLogin(kc); };
        return {
            get:   <T,>(path: string)                 => request<T>(ensureFresh, token, onAuthFail, 'GET',    path),
            post:  <T,>(path: string, body?: unknown) => request<T>(ensureFresh, token, onAuthFail, 'POST',   path, body),
            put:   <T,>(path: string, body?: unknown) => request<T>(ensureFresh, token, onAuthFail, 'PUT',    path, body),
            patch: <T,>(path: string, body?: unknown) => request<T>(ensureFresh, token, onAuthFail, 'PATCH',  path, body),
            del:   <T,>(path: string)                 => request<T>(ensureFresh, token, onAuthFail, 'DELETE', path),
            // Multipart form upload — used by the photo library. Skips the JSON
            // Content-Type header so the browser adds the boundary itself.
            postForm: async <T,>(path: string, form: FormData): Promise<T> => {
                await ensureFresh();
                const headers: Record<string, string> = {};
                const t = token();
                if (t) headers.Authorization = 'Bearer ' + t;
                const res = await fetch(path, { method: 'POST', headers, body: form });
                if (res.status === 401 && t) onAuthFail();
                if (!res.ok) throw new ApiError(res.status, await res.text());
                if (res.status === 204) return undefined as T;
                return (await res.json()) as T;
            },
        };
    }, [ensureFresh, token, kc]);
}

export type Api = ReturnType<typeof useApi>;
